import React, { useContext, useEffect, useState } from 'react';
import { Modal, List, Avatar, Typography, Button, Tabs, Tag, message, Tooltip, Collapse, Layout, Progress, Badge, Space, Card, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, CodeOutlined, DeleteOutlined, UserOutlined, ExperimentOutlined } from '@ant-design/icons';
import { AuthContext } from '../../../../config/AuthContext';
import Editor from '@monaco-editor/react';
import { GetCazaAlumnosAdmin } from '../../../../dao/jutge/GetCazaAlumnosAdmin';
import { GetCazaHistoryAdmin } from '../../../../dao/jutge/GetCazaHistoryAdmin';
import { DeleteCazaAttempt } from '../../../../dao/jutge/DeleteCazaAttempt';
import { GetJutgeCazaTests } from '../../../../dao/jutge/GetJutgeCazaTests';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Sider, Content } = Layout;

interface CazaAdminModalProps {
    visible: boolean;
    onClose: () => void;
    idCaza: string;
    cazaTitle: string;
    caza?: any;
    idConjunt?: number | null;
}

const CazaAdminModal: React.FC<CazaAdminModalProps> = ({ visible, onClose, idCaza, cazaTitle, caza, idConjunt }) => {
    const { user } = useContext(AuthContext);
    const [students, setStudents] = useState<any[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const [history, setHistory] = useState<any[]>([]);
    const [cazaTests, setCazaTests] = useState<any[]>([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Calculate total steps in this pildora
    const calculateTotalSteps = (): number => {
        if (!caza || !caza.instrucciones) return 1;
        try {
            const parsed = JSON.parse(caza.instrucciones);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed.length;
        } catch (e) {
            // Not json
        }
        const matches = caza.instrucciones.match(/(?:###\s*Paso|\bPASO\s+\d+:?)/gi);
        if (matches && matches.length > 0) return matches.length;
        return 1;
    };

    const totalSteps = calculateTotalSteps();

    useEffect(() => {
        if (visible) {
            fetchStudents();
            fetchTests();
        } else {
            setStudents([]);
            setSelectedStudent(null);
            setHistory([]);
            setCazaTests([]);
        }
    }, [visible, idCaza]);

    const fetchTests = async () => {
        if (!idCaza) return;
        try {
            const tests = await GetJutgeCazaTests(idCaza);
            setCazaTests(Array.isArray(tests) ? tests : []);
        } catch (e) {
            console.error("Error fetching tests in CazaAdminModal:", e);
        }
    };

    useEffect(() => {
        if (selectedStudent) {
            fetchHistory(selectedStudent.id);
        }
    }, [selectedStudent]);

    const fetchStudents = async () => {
        setLoadingStudents(true);
        const data = await GetCazaAlumnosAdmin(idCaza, idConjunt);
        setStudents(data || []);
        setLoadingStudents(false);
        return data || [];
    };

    const fetchHistory = async (idUsuari: string) => {
        setLoadingHistory(true);
        const data = await GetCazaHistoryAdmin(idCaza, idUsuari, idConjunt);
        // Ensure reverse chronological order: newest submission first
        const sorted = Array.isArray(data)
            ? [...data].sort((a, b) => {
                const timeDiff = new Date(b.moment || 0).getTime() - new Date(a.moment || 0).getTime();
                if (timeDiff !== 0) return timeDiff;
                return (Number(b.id) || 0) - (Number(a.id) || 0);
            })
            : [];
        setHistory(sorted);
        setLoadingHistory(false);
    };

    const handleDelete = async (attemptId: string) => {
        try {
            await DeleteCazaAttempt(attemptId);
            message.success('Intento eliminado y estadísticas recalculadas');
            // Refresh history for selected student
            if (selectedStudent) {
                fetchHistory(selectedStudent.id);
            }
            // Refresh students list and update currently selected student stats
            const updatedStudents = await fetchStudents();
            if (selectedStudent) {
                const refreshed = updatedStudents.find((s: any) => s.id === selectedStudent.id);
                if (refreshed) {
                    setSelectedStudent(refreshed);
                }
            }
        } catch (error) {
            message.error('Error al eliminar intento');
        }
    };

    // Only Admins (1) or Teachers (0) can delete
    const canDelete = user?.tipus === '0' || user?.tipus === '1';

    return (
        <Modal
            title={`Estadísticas de la Píldora: ${cazaTitle} (${totalSteps} ${totalSteps === 1 ? 'paso' : 'pasos'})`}
            open={visible}
            onCancel={onClose}
            width={1100}
            footer={null}
            bodyStyle={{ padding: 0, height: '75vh', overflow: 'hidden' }}
        >
            <Layout style={{ height: '100%', background: 'white' }}>
                <Sider width={340} theme="light" style={{ borderRight: '1px solid #f0f0f0', overflowY: 'auto' }}>
                    <div style={{ padding: '12px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>
                        Alumnos ({students.length})
                    </div>
                    <List
                        loading={loadingStudents}
                        itemLayout="horizontal"
                        dataSource={students}
                        renderItem={item => {
                            const completedSteps = item.paso_actual || 0;
                            const isCompleted = item.obtained === 1 || completedSteps >= totalSteps;
                            const percent = Math.min(100, Math.round((completedSteps / totalSteps) * 100));

                            return (
                                <List.Item
                                    style={{
                                        cursor: 'pointer',
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f5f5f5',
                                        background: selectedStudent?.id === item.id ? '#e6f7ff' : 'transparent'
                                    }}
                                    onClick={() => setSelectedStudent(item)}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                icon={<UserOutlined />}
                                                style={{
                                                    backgroundColor: isCompleted ? '#52c41a' : completedSteps > 0 ? '#1890ff' : '#d9d9d9'
                                                }}
                                            />
                                        }
                                        title={
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text strong style={{ fontSize: '0.9rem' }}>{item.nom}</Text>
                                                {isCompleted ? (
                                                    <Tag color="green" style={{ margin: 0 }}>Completado</Tag>
                                                ) : (
                                                    <Tag color={completedSteps > 0 ? "blue" : "default"} style={{ margin: 0 }}>
                                                        Paso {completedSteps}/{totalSteps}
                                                    </Tag>
                                                )}
                                            </div>
                                        }
                                        description={
                                            <div style={{ marginTop: 4 }}>
                                                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{item.email}</div>
                                                <Progress
                                                    percent={percent}
                                                    size="small"
                                                    status={isCompleted ? "success" : "active"}
                                                    showInfo={false}
                                                />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888', marginTop: 2 }}>
                                                    <span>Pasos: {completedSteps} de {totalSteps}</span>
                                                    <span>Intentos: {item.intentos_restantes}</span>
                                                </div>
                                                {item.tiempo_empleado_segundos !== undefined && item.tiempo_empleado_segundos !== null && (
                                                    <div style={{ fontSize: 11, color: '#1890ff', marginTop: 2 }}>
                                                        ⏱️ Tiempo: {Math.floor(item.tiempo_empleado_segundos / 60)}m {item.tiempo_empleado_segundos % 60}s
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </Sider>
                <Content style={{ padding: '20px', overflowY: 'auto' }}>
                    {selectedStudent ? (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>{selectedStudent.nom}</Title>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{selectedStudent.email}</Text>
                                    {selectedStudent.primer_intento && (
                                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                            Primer intento: {selectedStudent.primer_intento}
                                            {selectedStudent.resolucion_intento && ` | Resolución: ${selectedStudent.resolucion_intento}`}
                                        </div>
                                    )}
                                </div>
                                <Space>
                                    <Tag color={selectedStudent.obtained === 1 ? 'green' : selectedStudent.paso_actual > 0 ? 'blue' : 'orange'} style={{ fontSize: 13, padding: '4px 8px' }}>
                                        {selectedStudent.obtained === 1
                                            ? 'PÍLDORA COMPLETADA'
                                            : `Paso actual: ${selectedStudent.paso_actual || 0} de ${totalSteps}`}
                                    </Tag>
                                    <Tag color="cyan">Intentos restantes: {selectedStudent.intentos_restantes}</Tag>
                                    {selectedStudent.tiempo_empleado_segundos !== undefined && selectedStudent.tiempo_empleado_segundos !== null && (
                                        <Tag color="geekblue" style={{ fontSize: 13, padding: '4px 8px' }}>
                                            ⏱️ {Math.floor(selectedStudent.tiempo_empleado_segundos / 60)}m {selectedStudent.tiempo_empleado_segundos % 60}s
                                        </Tag>
                                    )}
                                </Space>
                            </div>

                            {loadingHistory ? (
                                <div style={{ textAlign: 'center', marginTop: 50 }}>Cargando historial de envíos...</div>
                            ) : history.length > 0 ? (
                                <Collapse accordion defaultActiveKey={[history[0]?.id]}>
                                    {history.map((attempt) => {
                                        const isPassed = attempt.estat === 1;
                                        const isPending = attempt.estat === -1;
                                        const stepNum = attempt.ordre || 1;

                                        return (
                                            <Panel
                                                header={
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                        <Space>
                                                            <Tag color="purple" style={{ fontWeight: 'bold' }}>
                                                                Paso {stepNum}
                                                            </Tag>
                                                            <Tag color={isPassed ? 'green' : isPending ? 'orange' : 'red'}>
                                                                {isPassed ? 'CORRECTO' : isPending ? 'EVALUANDO' : 'INCORRECTO'}
                                                            </Tag>
                                                            <span style={{ fontSize: 12, color: '#666' }}>{attempt.moment}</span>
                                                        </Space>
                                                        {canDelete && (
                                                            <Tooltip title="Eliminar intento">
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(attempt.id);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                }
                                                key={attempt.id}
                                            >
                                                <div style={{ marginBottom: 12 }}>
                                                    <strong>Intentos registrados en ese momento:</strong> {attempt.intent}
                                                </div>

                                                {/* Tests del Paso definidos por el profesor */}
                                                {(() => {
                                                    const stepTests = cazaTests.filter(t => (t.ordre || 1) === stepNum);
                                                    if (stepTests.length === 0) return null;

                                                    return (
                                                        <div style={{ marginBottom: 16 }}>
                                                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#1d39c4', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <ExperimentOutlined /> Tests configurados para el Paso {stepNum} ({stepTests.length}):
                                                            </div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: stepTests.length > 1 ? '1fr 1fr' : '1fr', gap: 8 }}>
                                                                {stepTests.map((t, idx) => {
                                                                    const tipus = (t.tipus || "es").toLowerCase();
                                                                    let input = "";
                                                                    let expected = "";
                                                                    let tagColor = "blue";
                                                                    let typeLabel = "E/S";

                                                                    if (tipus === "content") {
                                                                        tagColor = "green";
                                                                        typeLabel = "CONTENIDO";
                                                                        try {
                                                                            let jsonStr = "";
                                                                            if (t.fitxer?.startsWith("CONTENT:DATA:")) {
                                                                                const rawB64 = t.fitxer.substring("CONTENT:DATA:".length).trim();
                                                                                jsonStr = window.atob(rawB64);
                                                                            } else if (t.fitxer?.startsWith("{")) {
                                                                                jsonStr = t.fitxer;
                                                                            }
                                                                            if (jsonStr) {
                                                                                const parsed = JSON.parse(jsonStr);
                                                                                expected = (parsed.mustContain !== false ? "Debe contener: " : "No debe contener: ") + `"${parsed.contentText || ''}"`;
                                                                            }
                                                                        } catch (e) {
                                                                            expected = t.fitxer;
                                                                        }
                                                                    } else if (tipus === "method" || tipus === "methodtest") {
                                                                        tagColor = "purple";
                                                                        typeLabel = "MÉTODO";
                                                                        try {
                                                                            let jsonStr = "";
                                                                            if (t.fitxer?.startsWith("METHOD:DATA:")) {
                                                                                const rawB64 = t.fitxer.substring("METHOD:DATA:".length).trim();
                                                                                jsonStr = window.atob(rawB64);
                                                                            } else if (t.fitxer?.startsWith("{")) {
                                                                                jsonStr = t.fitxer;
                                                                            }
                                                                            if (jsonStr) {
                                                                                const parsed = JSON.parse(jsonStr);
                                                                                expected = `Método: ${parsed.returnType || 'void'} ${parsed.methodName || ''}(...)`;
                                                                                if (parsed.entrada) input = parsed.entrada;
                                                                                if (parsed.sortida) expected += ` -> Retorna/Imprime: ${parsed.sortida}`;
                                                                            }
                                                                        } catch (e) {
                                                                            expected = t.fitxer;
                                                                        }
                                                                    } else {
                                                                        // E/S
                                                                        if (t.fitxer) {
                                                                            const parts = t.fitxer.split(":", 3);
                                                                            if (parts.length >= 2) {
                                                                                try {
                                                                                    input = window.atob(parts[1].trim());
                                                                                } catch (e) {
                                                                                    input = parts[1];
                                                                                }
                                                                            }
                                                                            if (parts.length >= 3) {
                                                                                try {
                                                                                    expected = window.atob(parts[2].trim());
                                                                                } catch (e) {
                                                                                    expected = parts[2];
                                                                                }
                                                                            }
                                                                        }
                                                                    }

                                                                    return (
                                                                        <div key={t.id || idx} style={{
                                                                            background: '#f0f5ff',
                                                                            border: '1px solid #adc6ff',
                                                                            borderRadius: 6,
                                                                            padding: 8,
                                                                            fontSize: 12
                                                                        }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                                                <span style={{ fontWeight: 600, color: '#1d39c4' }}>
                                                                                    {t.name || `Test #${idx + 1}`}
                                                                                </span>
                                                                                <Tag color={tagColor} style={{ fontSize: 10, lineHeight: '18px', padding: '0 4px', margin: 0 }}>
                                                                                    {typeLabel}
                                                                                </Tag>
                                                                            </div>
                                                                            {input && input.trim().length > 0 && (
                                                                                <div style={{ marginBottom: 4 }}>
                                                                                    <span style={{ color: '#595959', fontWeight: 500 }}>Entrada: </span>
                                                                                    <pre style={{ margin: 0, padding: '2px 4px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, maxHeight: 60, overflow: 'auto' }}>
                                                                                        {input}
                                                                                    </pre>
                                                                                </div>
                                                                            )}
                                                                            <div>
                                                                                <span style={{ color: '#595959', fontWeight: 500 }}>
                                                                                    {tipus === 'content' ? 'Condición de código: ' : 'Salida esperada: '}
                                                                                </span>
                                                                                <pre style={{ margin: 0, padding: '2px 4px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, maxHeight: 60, overflow: 'auto' }}>
                                                                                    {expected || "(vacía)"}
                                                                                </pre>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                <div style={{ marginBottom: 16 }}>
                                                    <strong>Resultado / Salida de la validación:</strong>
                                                    <pre style={{
                                                        background: isPassed ? '#f6ffed' : '#fff1f0',
                                                        border: isPassed ? '1px solid #b7eb8f' : '1px solid #ffa39e',
                                                        padding: 10,
                                                        borderRadius: 4,
                                                        maxHeight: 160,
                                                        overflow: 'auto',
                                                        color: isPassed ? '#389e0d' : '#cf1322',
                                                        whiteSpace: 'pre-wrap',
                                                        marginTop: 4
                                                    }}>
                                                        {attempt.errors && attempt.errors.trim().length > 0 && (!attempt.errors.trim().startsWith("OK") || isPassed)
                                                            ? attempt.errors
                                                            : (isPassed
                                                                ? "Todos los tests superados correctamente"
                                                                : "El código no produjo el resultado esperado para las pruebas de este paso.")}
                                                    </pre>
                                                </div>
                                                <div>
                                                    <strong>Código enviado por el alumno:</strong>
                                                    <div style={{ height: 320, border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
                                                        <Editor
                                                            height="100%"
                                                            defaultLanguage="java"
                                                            value={attempt.actual || "// No code"}
                                                            options={{ readOnly: true, minimap: { enabled: false } }}
                                                        />
                                                    </div>
                                                </div>
                                            </Panel>
                                        );
                                    })}
                                </Collapse>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#888', marginTop: 50 }}>
                                    Este alumno aún no ha realizado ningún envío en esta píldora.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888' }}>
                            <div style={{ textAlign: 'center' }}>
                                <UserOutlined style={{ fontSize: 40, marginBottom: 10 }} />
                                <div>Selecciona un alumno para ver sus pasos completados y código enviado</div>
                            </div>
                        </div>
                    )}
                </Content>
            </Layout>
        </Modal>
    );
};

export default CazaAdminModal;
